/**
 * uploadValidation — shared validators for the presence wizard
 *
 * Every validator returns either:
 *   - { ok: true, ...meta }              on success
 *   - { ok: false, code: "xxx" }         on a known, user-actionable problem
 *
 * The UI maps `code` to a localized message via the `wiz.*_err_*` keys. We
 * never throw or alert — the caller renders an inline guided error card so
 * the flow stays on the page, no dead ends.
 *
 * Keep these numbers in sync with the backend whitelists in server.py
 * (`/presences/{id}/voice`).
 */

// ── Photo ──────────────────────────────────────────────────────────────────
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;     // 10MB
export const PHOTO_MIN_DIMENSION = 400;              // px (shortest edge)
export const PHOTO_ACCEPTED_MIME = new Set([
    "image/jpeg", "image/jpg", "image/png", "image/webp",
]);

export async function validatePhoto(file) {
    if (!file) return { ok: false, code: "corrupt" };

    // 1) Format — accept the common web-safe set. HEIC/HEIF are rejected
    //    because browsers can't reliably decode them for the preview.
    const mime = (file.type || "").toLowerCase();
    if (!PHOTO_ACCEPTED_MIME.has(mime)) return { ok: false, code: "format" };

    // 2) Size — hard cap before we try to decode the image.
    if (file.size > PHOTO_MAX_BYTES) return { ok: false, code: "too_large" };

    // 3) Dimensions — load the image and verify shortest edge ≥ min.
    //    If decoding throws or dimensions are 0, the file is effectively
    //    corrupt (or an unsupported animated format masquerading as jpg).
    let dims;
    try {
        dims = await readImageDimensions(file);
    } catch {
        return { ok: false, code: "corrupt" };
    }
    if (!dims || !dims.width || !dims.height) return { ok: false, code: "corrupt" };
    const shortest = Math.min(dims.width, dims.height);
    if (shortest < PHOTO_MIN_DIMENSION) return { ok: false, code: "too_small" };

    return { ok: true, width: dims.width, height: dims.height };
}

function readImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const out = { width: img.naturalWidth, height: img.naturalHeight };
            URL.revokeObjectURL(url);
            resolve(out);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("decode failed"));
        };
        img.src = url;
    });
}

// ── Voice ──────────────────────────────────────────────────────────────────
export const VOICE_MAX_BYTES = 25 * 1024 * 1024;     // 25MB
export const VOICE_MIN_SECONDS = 10;
export const VOICE_ACCEPTED_MIME = new Set([
    "audio/mpeg", "audio/mp3", "audio/wav", "audio/wave", "audio/x-wav",
    "audio/mp4", "audio/x-m4a", "audio/m4a",
    "audio/webm", "audio/ogg", "audio/oga",
]);

export async function validateVoiceFile(file) {
    if (!file) return { ok: false, code: "corrupt" };

    const mime = (file.type || "").toLowerCase();
    // Some mobile recorders report empty MIME — fall back to the extension.
    const ext = (file.name || "").toLowerCase().split(".").pop();
    const extOk = ["mp3", "wav", "m4a", "mp4", "ogg", "oga", "webm"].includes(ext);
    if (!VOICE_ACCEPTED_MIME.has(mime) && !extOk) {
        return { ok: false, code: "format" };
    }
    if (file.size > VOICE_MAX_BYTES) return { ok: false, code: "too_large" };

    // Duration check — the only reliable cross-browser way is to create a
    // throw-away <audio> element and wait for loadedmetadata. This also
    // doubles as a corruption check: if the decoder can't read metadata,
    // the file is unusable.
    let seconds;
    try {
        seconds = await readAudioDuration(file);
    } catch {
        return { ok: false, code: "corrupt" };
    }
    if (!isFinite(seconds) || seconds <= 0) return { ok: false, code: "corrupt" };
    if (seconds < VOICE_MIN_SECONDS) return { ok: false, code: "too_short", seconds };

    return { ok: true, seconds };
}

function readAudioDuration(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const audio = document.createElement("audio");
        let settled = false;
        const done = (fn) => (...args) => {
            if (settled) return;
            settled = true;
            URL.revokeObjectURL(url);
            fn(...args);
        };
        audio.preload = "metadata";
        audio.onloadedmetadata = done(() => resolve(audio.duration));
        audio.onerror = done(() => reject(new Error("decode failed")));
        // Safari occasionally reports duration=Infinity until a seek happens.
        // Give the decoder a moment, then give up.
        setTimeout(done(() => reject(new Error("timeout"))), 10000);
        audio.src = url;
    });
}
